import React from "react";
import {
  Alert,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const cell = { border: "1px solid #e0e0e0", fontSize: "13px" };
const headCell = { ...cell, fontWeight: 600, backgroundColor: "#f5f5f5" };

/**
 * What the admit card PDF will actually print, straight from GET /preview/:courseid —
 * same reader and same formatters the renderer uses, so this is the output, not a guess.
 */
export default function PreviewPanel({ preview }) {
  if (!preview) {
    return (
      <Alert severity="info" sx={{ fontSize: "13px" }}>
        Select a course to see what the admit card will print.
      </Alert>
    );
  }

  const { layout, configured, usedByAdmitCard, note, sections, papers, breakColumn } = preview;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Chip size="small" label={`Layout: ${layout}`} />
        <Chip
          size="small"
          label={configured ? "Configured" : "Not configured"}
          color={configured ? "success" : "warning"}
        />
        {configured && (
          <Chip
            size="small"
            label={usedByAdmitCard ? "Used by admit card" : "Not used by admit card"}
            color={usedByAdmitCard ? "success" : "default"}
          />
        )}
      </Box>

      {note && (
        <Alert severity="info" sx={{ fontSize: "13px", mb: 2 }}>
          {note}
        </Alert>
      )}

      {sections?.map((section) => (
        <Box key={section?.sectionNo} mb={3}>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 1 }}>
            {section?.title || `Section ${section?.sectionNo}`}
          </Typography>
          {section?.rows?.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  {section?.rows?.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ ...cell, width: "45%" }}>{row?.label}</TableCell>
                      <TableCell sx={cell}>{row?.printed}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {section?.instructions?.map((instruction, i) => (
            <Typography key={i} sx={{ fontSize: "13px", mt: 1, fontStyle: "italic" }}>
              {instruction}
            </Typography>
          ))}
        </Box>
      ))}

      {papers?.length > 0 && (
        <Box mb={2}>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 1 }}>
            Paper-wise grid
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell}>Paper</TableCell>
                  <TableCell sx={headCell}>Name</TableCell>
                  <TableCell sx={headCell}>Date</TableCell>
                  <TableCell sx={headCell}>Reporting</TableCell>
                  <TableCell sx={headCell}>Gate Closure</TableCell>
                  <TableCell sx={headCell}>Schedule</TableCell>
                  <TableCell sx={headCell}>Marks</TableCell>
                  <TableCell sx={headCell}>Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {papers?.map((paper) => (
                  <TableRow key={paper?.paperNo}>
                    <TableCell sx={cell}>{paper?.paperLabel}</TableCell>
                    <TableCell sx={cell}>{paper?.paperName}</TableCell>
                    <TableCell sx={cell}>{paper?.date}</TableCell>
                    <TableCell sx={cell}>{paper?.reportingTime}</TableCell>
                    <TableCell sx={cell}>{paper?.gateClosure}</TableCell>
                    <TableCell sx={cell}>{paper?.schedule}</TableCell>
                    <TableCell sx={cell}>{paper?.marks}</TableCell>
                    <TableCell sx={cell}>{paper?.duration}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {breakColumn && (
        <Typography sx={{ fontSize: "13px" }}>
          <strong>{breakColumn?.name}</strong> — {breakColumn?.window}
        </Typography>
      )}
    </Box>
  );
}
